import { FC, useCallback, useEffect, useRef } from 'react';

import '@telialt/assets/resources/sass/icons.scss';
import { ENABLE_THIRD_PARTY_LIVE_CHAT_WIDGET } from '@telialt/common-constants/src/ENABLE_THIRD_PARTY_LIVE_CHAT_WIDGET';
import {
    LiveChatWidget,
    StickyPageActions,
    useRegisterLiveChatAction,
    useRegisterScrollToTopAction,
} from '@telialt/core-sticky-page-actions/src';
import { isSSR } from '@telialt/utils/src/helpers';
import { useUserDataQueryBase } from '@telialt/utils/src/SSO/useUserData';

const PageActions: FC = () => {
    const { userData } = useUserDataQueryBase({ isEnabled: !isSSR() });

    const liveChatLicenseId = process.env.LIVE_CHAT_LICENSE_ID;

    useRegisterScrollToTopAction();
    useRegisterLiveChatAction({
        sessionToken: userData?.sessionToken,
        businessPartnerId: userData?.id,
    });

    return (
        <>
            <StickyPageActions />
            <LiveChatWidget license={liveChatLicenseId} />
        </>
    );
};

const LiveChatRemoteControlled = () => {
    const { userData } = useUserDataQueryBase({ isEnabled: !isSSR() });
    const liveChatLicenseId = process.env.LIVE_CHAT_LICENSE_ID;
    const isReadyRef = useRef(false);

    const updateSessionVariables = useCallback(() => {
        if (!isReadyRef.current || userData === undefined) {
            return;
        }
        const customerData = window.LiveChatWidget?.get('customer_data');
        const previousSessionVariables = customerData?.sessionVariables;
        const { sessionToken, businessPartnerId } = previousSessionVariables || {};
        const hasSessionTokenChanged = sessionToken !== userData?.sessionToken;
        const hasBusinessPartnerIdChanged = businessPartnerId !== userData?.id;

        const updatedSessionVariables = {
            sessionToken:
                !sessionToken || sessionToken === '1' || hasSessionTokenChanged
                    ? (userData?.sessionToken ?? '1')
                    : sessionToken,
            businessPartnerId:
                !businessPartnerId || businessPartnerId === '1' || hasBusinessPartnerIdChanged
                    ? (userData?.id ?? '1')
                    : businessPartnerId,
        };

        window.LiveChatWidget?.call('update_session_variables', updatedSessionVariables);
    }, [userData]);

    const handleLiveChatReady = () => {
        if (isSSR()) {
            return;
        }
        isReadyRef.current = true;
        updateSessionVariables();
    };

    useEffect(() => {
        updateSessionVariables();
    }, [userData, updateSessionVariables]);

    return <LiveChatWidget license={liveChatLicenseId} onReady={handleLiveChatReady} />;
};

const PageActionsToggled = () => {
    if (ENABLE_THIRD_PARTY_LIVE_CHAT_WIDGET) {
        return <LiveChatRemoteControlled />;
    }

    return <PageActions />;
};

export { PageActionsToggled as PageActions };
